import React, { useState, useEffect } from 'react';
import {
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Chip,
  FormHelperText,
  IconButton,
  InputAdornment,
  Grid,
  Slider,
  Tooltip,
  Alert,
  Autocomplete,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Close as CloseIcon,
  Event as EventIcon,
  Flag as FlagIcon,
  Info as InfoIcon,
  Save as SaveIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import ProModal from '../../common/ProModal';

const DEFAULT_SETTINGS = {
  startYear: 0,
  endYear: 1000,
  yearLabel: 'CE',
  displayYears: true
};

const TimelineEventDialog = ({ 
  open, 
  onClose, 
  event = null, 
  timeline = { events: [], eras: [], settings: DEFAULT_SETTINGS }, 
  onSave = () => {}
}) => {
  // Ensure timeline has all required properties
  const completeTimeline = {
    events: timeline.events || [],
    eras: timeline.eras || [],
    settings: timeline.settings || DEFAULT_SETTINGS
  };
  
  // Calculate the middle year for default value
  const defaultYear = Math.round((completeTimeline.settings.startYear + completeTimeline.settings.endYear) / 2);
  
  const [editedEvent, setEditedEvent] = useState({
    id: '',
    title: '',
    description: '',
    year: defaultYear,
    date: '',
    era: '',
    importance: 'medium',
    tags: [],
    category: 'timeline',
    details: '',
    consequences: '',
    participants: '',
    location: ''
  });
  
  const [errors, setErrors] = useState({});
  const [newTag, setNewTag] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [expandedSection, setExpandedSection] = useState(null);

  const importanceLevels = [
    { value: 'low', label: 'Low', color: 'success.main' },
    { value: 'medium', label: 'Medium', color: 'warning.main' },
    { value: 'high', label: 'High', color: 'error.main' }
  ];

  const handleSectionToggle = (section) => (event, isExpanded) => {
    setExpandedSection(isExpanded ? section : null);
  };

  // Initialize the form with the event data when it changes
  useEffect(() => {
    if (event) {
      // Editing existing event
      setEditedEvent({
        id: event.id || Date.now().toString(),
        title: event.title || '',
        description: event.description || '',
        year: event.year || defaultYear,
        date: event.date || '',
        era: event.era || '',
        importance: event.importance || 'medium',
        tags: event.tags || [],
        category: event.category || 'timeline',
        details: event.details || '',
        consequences: event.consequences || '',
        participants: event.participants || '',
        location: event.location || ''
      });
    } else {
      // Default values for a new event
      setEditedEvent({
        id: Date.now().toString(),
        title: '',
        description: '',
        year: defaultYear,
        date: '',
        era: completeTimeline.eras.length > 0 ? completeTimeline.eras[0]?.id || '' : '',
        importance: 'medium',
        tags: [],
        category: 'timeline',
        details: '',
        consequences: '',
        participants: '',
        location: ''
      });
    }
  }, [event, open, completeTimeline.settings.startYear, completeTimeline.settings.endYear, defaultYear]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditedEvent({
      ...editedEvent,
      [name]: value
    });
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  const handleImportanceChange = (event, newValue) => {
    const importanceMap = {
      1: 'low',
      2: 'medium',
      3: 'high'
    };
    
    setEditedEvent({
      ...editedEvent,
      importance: importanceMap[newValue]
    });
  };
  
  const getImportanceValue = (importance) => {
    const importanceMap = {
      'low': 1,
      'medium': 2,
      'high': 3
    };
    
    return importanceMap[importance] || 2;
  };

  const handleAddTag = () => {
    if (newTag.trim() && !editedEvent.tags.includes(newTag.trim())) {
      setEditedEvent({
        ...editedEvent,
        tags: [...editedEvent.tags, newTag.trim()]
      });
      setNewTag('');
    }
  };

  const handleDeleteTag = (tagToDelete) => {
    setEditedEvent({
      ...editedEvent,
      tags: editedEvent.tags.filter(tag => tag !== tagToDelete)
    });
  };

  const handleTagsChange = (event, newValue) => {
    setEditedEvent({ ...editedEvent, tags: newValue });
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!editedEvent.title.trim()) {
      newErrors.title = 'Title is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setIsSaving(true);
    setSaveError(null);

    try {
      if (typeof onSave === 'function') {
        await onSave(editedEvent);
      }
      onClose();
    } catch (error) {
      console.error('Error saving event:', error);
      setSaveError('Failed to save event. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const accentColor = isDarkMode ? '#ffcc80' : '#fb8c00';
  const accentBg = isDarkMode ? 'rgba(255, 183, 77, 0.12)' : 'rgba(255, 183, 77, 0.08)';
  const accentBorder = isDarkMode ? 'rgba(255, 213, 153, 0.3)' : 'rgba(255, 152, 0, 0.2)';
  const chipBg = isDarkMode ? 'rgba(255, 138, 101, 0.25)' : 'rgba(255, 138, 101, 0.18)';

  return (
    <ProModal 
      open={open} 
      onClose={isSaving ? undefined : onClose} 
      title={event ? 'Edit Timeline Event' : 'Add Timeline Event'}
      eyebrow="TIMELINE"
      icon={<EventIcon />}
      maxWidth="md"
      disableCloseButton={isSaving}
      actions={(
        <>
          <Button onClick={onClose} disabled={isSaving} variant="outlined" startIcon={<CloseIcon />} sx={{ borderRadius: '8px', textTransform: 'none' }}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            disabled={isSaving}
            startIcon={<SaveIcon />}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              background: 'linear-gradient(120deg, #ff9800, #fb8c00)',
              '&:hover': {
                background: 'linear-gradient(120deg, #fb8c00, #ef6c00)'
              }
            }}
          >
            {isSaving ? 'Saving...' : 'Save Event'}
          </Button>
        </>
      )}
      contentProps={{ sx: { pt: 2, pb: 0 } }}
    >
      {saveError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {saveError}
        </Alert>
      )}
      <Box sx={{ pt: 1 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              autoFocus
              margin="dense"
              name="title"
              label="Event Title"
              type="text"
              fullWidth
              variant="outlined"
              value={editedEvent.title || ''}
              onChange={handleChange}
              error={!!errors.title}
              helperText={errors.title}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FlagIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              margin="dense"
              name="year"
              label="Year"
              type="number"
              fullWidth
              variant="outlined"
              value={editedEvent.year || defaultYear}
              onChange={handleChange}
              InputProps={{
                inputProps: { 
                  min: completeTimeline.settings.startYear,
                  max: completeTimeline.settings.endYear
                }
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth variant="outlined" margin="dense">
              <InputLabel id="era-select-label">Era</InputLabel>
              <Select
                labelId="era-select-label"
                name="era"
                value={editedEvent.era || ''}
                onChange={handleChange}
                label="Era"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {completeTimeline.eras && completeTimeline.eras.map((era) => (
                  <MenuItem key={era.id} value={era.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {era.color && (
                        <Box 
                          sx={{ 
                            width: 12, 
                            height: 12, 
                            borderRadius: '50%', 
                            bgcolor: era.color,
                            mr: 1
                          }} 
                        />
                      )}
                      {era.name}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={8}>
            <TextField
              margin="dense"
              name="date"
              label="Date or Time Period"
              type="text"
              fullWidth
              variant="outlined"
              value={editedEvent.date || ''}
              onChange={handleChange}
              placeholder="e.g., '1242', 'Summer of the Third Age', etc."
            />
          </Grid>
          
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Typography variant="subtitle1" sx={{ mr: 1 }}>
                Importance:
              </Typography>
              {importanceLevels.map((level) => (
                <Chip
                  key={level.value}
                  label={level.label}
                  onClick={() => setEditedEvent({ ...editedEvent, importance: level.value })}
                  variant={editedEvent.importance === level.value ? 'filled' : 'outlined'}
                  sx={{ 
                    cursor: 'pointer',
                    borderColor: level.color,
                    color: editedEvent.importance === level.value ? 'common.white' : level.color,
                    bgcolor: editedEvent.importance === level.value ? level.color : 'transparent'
                  }}
                />
              ))}
            </Box>
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              margin="dense"
              name="description"
              label="Description"
              type="text"
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              value={editedEvent.description || ''}
              onChange={handleChange}
            />
          </Grid>
          
          <Grid item xs={12}>
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={editedEvent.tags || []}
              onChange={handleTagsChange}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option}
                    {...getTagProps({ index })}
                    key={option}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tags"
                  placeholder="Add tags"
                  helperText="Press Enter to add a tag"
                  fullWidth
                  variant="outlined"
                  margin="dense"
                />
              )}
            />
          </Grid>
          
          <Grid item xs={12}>
            <Accordion 
              expanded={expandedSection === 'details'} 
              onChange={handleSectionToggle('details')}
              elevation={0}
              sx={{ 
                border: '1px solid rgba(0,0,0,0.1)', 
                borderRadius: '8px',
                '&:before': {
                  display: 'none',
                },
                mb: 1
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ 
                  borderRadius: '8px',
                  '&.Mui-expanded': {
                    borderBottomLeftRadius: 0,
                    borderBottomRightRadius: 0
                  }
                }}
              >
                <Typography variant="subtitle1">Additional Details</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      margin="dense"
                      name="details"
                      label="Event Details"
                      type="text"
                      fullWidth
                      multiline
                      rows={3}
                      variant="outlined"
                      value={editedEvent.details || ''}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      margin="dense"
                      name="consequences"
                      label="Consequences"
                      type="text"
                      fullWidth
                      multiline
                      rows={3}
                      variant="outlined"
                      value={editedEvent.consequences || ''}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      margin="dense"
                      name="participants"
                      label="Participants"
                      type="text"
                      fullWidth
                      variant="outlined"
                      value={editedEvent.participants || ''}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      margin="dense"
                      name="location"
                      label="Location"
                      type="text"
                      fullWidth
                      variant="outlined"
                      value={editedEvent.location || ''}
                      onChange={handleChange}
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Grid>
        </Grid>
      </Box>
    </ProModal>
  );
};

TimelineEventDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  event: PropTypes.object,
  timeline: PropTypes.object,
  onSave: PropTypes.func
};

export default TimelineEventDialog;
